import React, { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import type { IconBaseProps } from 'react-icons';

interface WhatsAppChatButtonProps {
  phoneNumber: string;
  defaultMessage?: string;
  bottom?: string;
  right?: string;
  zIndex?: number;
}

const prettifySegment = (segment?: string): string =>
  (segment || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const WhatsAppChatButton: React.FC<WhatsAppChatButtonProps> = ({
  phoneNumber = '94773421388',
  defaultMessage = 'Hi Nex Computers! I need help selecting the right setup.',
  bottom = '24px',
  right = '24px',
  zIndex = 1200,
}) => {
  const location = useLocation();
  const params = useParams();

  const message = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const currentSection = pathSegments[0];

    if (currentSection === 'cart') {
      return 'Hi Nex Computers! I am ready to checkout and need quick help with my cart.';
    }

    if (['laptop', 'desktop', 'components', 'accessories', 'offers'].includes(currentSection || '')) {
      const routeProductName = prettifySegment(params.category || pathSegments[pathSegments.length - 1]);

      return routeProductName
        ? `Hi Nex Computers! I am interested in ${routeProductName}. Can you guide me on availability and best price?`
        : `Hi Nex Computers! I am browsing your ${prettifySegment(currentSection)} products and need recommendations.`;
    }

    return defaultMessage;
  }, [defaultMessage, location.pathname, params.category]);

  const chatUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={chatUrl}
      target="_blank"
      rel="noreferrer"
      aria-label="Chat on WhatsApp"
      className="fixed bottom-3 right-3 sm:bottom-5 sm:right-6 lg:bottom-6 lg:right-8"
      style={{ zIndex }}
    >
      <span className="absolute inset-0 rounded-full bg-emerald-400/70 animate-ping" />
      <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white shadow-2xl ring-2 ring-emerald-300/30 transition hover:scale-105 sm:h-14 sm:w-14 lg:h-16 lg:w-16 lg:ring-4">
        {React.createElement(FaWhatsapp as unknown as React.FC<IconBaseProps>, { size: 30 })}
      </span>
    </a>
  );
};

export default WhatsAppChatButton;
